/**
 * HealthTab - Agent health checks for the drawer
 *
 * Content migrated from AgentHealthModal. Shows:
 * - Health check boxes (nested virtualization, network, disk, RAM)
 * - Cloudflare tunnel status (if configured)
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Spinner } from '@/components/ui/spinner';
import type { Agent } from '@/hooks/useAgents';
import { CloudflareTunnelStatus } from '@/sections/agents/components/health/CloudflareTunnelStatus';
import { HealthCheckBox } from '@/sections/agents/components/health/HealthCheckBox';

interface HealthTabProps {
  agent: Agent | null;
  isLoading: boolean;
}

export const HealthTab: React.FC<HealthTabProps> = ({ agent, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-chariot-text-secondary flex min-h-[200px] items-center justify-center">
        Agent data not available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="agent-drawer-health">
      {/* Header - Always visible */}
      <div className="flex items-center gap-2 px-6 pt-6">
        <h3 className="text-chariot-text-primary text-lg font-semibold">
          Health Checks
        </h3>
        <Tooltip title="View endpoint system requirements and Cloudflare tunnel status">
          <div className="text-chariot-text-secondary cursor-help">
            <InformationCircleIcon className="size-4" />
          </div>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-2 px-6 pb-6">
        <HealthCheckBox agent={agent} />

        {agent.health_check?.cloudflared_status && (
          <CloudflareTunnelStatus
            cloudflaredStatus={agent.health_check.cloudflared_status}
          />
        )}
      </div>
    </div>
  );
};
